// frontend/src/core/modules/EnabledModulesProvider.tsx
//
// Fetches the tenant's enabled modules once and exposes them to the dashboard
// shell (nav filtering) and the RequireModule route guard. Mirrors the existing
// AuthProvider/BrandingProvider context pattern.

import * as React from 'react';
import { api } from '../api-client';

interface EnabledModulesValue {
  modules: Set<string>;
  subscriptionStatus: string | null;
  loading: boolean;
  /** True once the fetch has resolved (success OR failure) — used to avoid
   *  flashing the upsell card before we know what's enabled. */
  loaded: boolean;
}

const Ctx = React.createContext<EnabledModulesValue | null>(null);

export function EnabledModulesProvider({ children }: { children: React.ReactNode }) {
  const [modules, setModules] = React.useState<Set<string>>(new Set());
  const [subscriptionStatus, setSubscriptionStatus] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    api<{ modules: string[]; subscriptionStatus: string | null }>('/api/tenant/enabled-modules')
      .then((r) => {
        if (cancelled) return;
        setModules(new Set(r.modules));
        setSubscriptionStatus(r.subscriptionStatus);
      })
      .catch(() => {
        // On failure, fail OPEN (treat everything as enabled) so a transient
        // error never hides a paid module the tenant actually has. The
        // server-side requireModule() remains the real gate.
        if (!cancelled) setModules(new Set(ALL_MODULES));
      })
      .finally(() => { if (!cancelled) { setLoading(false); setLoaded(true); } });
    return () => { cancelled = true; };
  }, []);

  return (
    <Ctx.Provider value={{ modules, subscriptionStatus, loading, loaded }}>
      {children}
    </Ctx.Provider>
  );
}

export function useEnabledModules(): EnabledModulesValue {
  const v = React.useContext(Ctx);
  if (!v) throw new Error('useEnabledModules must be used inside <EnabledModulesProvider>');
  return v;
}

/** The 9 paid module keys — kept in sync with the backend DEFAULT_MODULES. */
export const ALL_MODULES = [
  'invoicing', 'hr', 'payroll', 'payments', 'projects', 'monitoring', 'email', 'expenses', 'esign',
] as const;
